import React, { useState, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const GymProvisioning = () => {
  const nextInstanceId = useRef(1);

  const [formData, setFormData] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    managerName: '',
    managerEmail: '',
    managerPhone: '',
    boards: [],
    boardInstances: [],
    planType: 'founder',
    priceCents: 4900,
    trialDays: 30
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const boardOptions = [
    { value: 'tension', label: 'Tension Board' },
    { value: 'kilter', label: 'Kilter Board' },
    { value: 'moon', label: 'Moon Board' },
    { value: 'grasshopper', label: 'Grasshopper' },
    { value: 'decoy', label: 'Decoy Board' },
    { value: 'custom', label: 'Custom Board' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const createInstance = (boardType, existingInstances) => {
    const boardLabel = boardOptions.find(b => b.value === boardType)?.label || boardType;
    const count = existingInstances.filter(i => i.type === boardType).length;
    return {
      id: nextInstanceId.current++,
      type: boardType,
      name: `${boardLabel} ${count + 1}`,
      angle: '',
      isAdjustable: false,
      minAngle: '',
      maxAngle: '',
      locationNotes: '',
      hasLed: true,
      status: 'active'
    };
  };

  const handleBoardToggle = (boardType) => {
    setFormData(prev => {
      const isRemoving = prev.boards.includes(boardType);

      if (isRemoving) {
        return {
          ...prev,
          boards: prev.boards.filter(b => b !== boardType),
          boardInstances: prev.boardInstances.filter(i => i.type !== boardType)
        };
      } else {
        const newInstance = createInstance(boardType, prev.boardInstances);
        return {
          ...prev,
          boards: [...prev.boards, boardType],
          boardInstances: [...prev.boardInstances, newInstance]
        };
      }
    });
  };

  const handleQuantityChange = (boardType, delta) => {
    setFormData(prev => {
      const typeInstances = prev.boardInstances.filter(i => i.type === boardType);
      const newCount = Math.max(1, typeInstances.length + delta);

      if (newCount > typeInstances.length) {
        const newInstances = [];
        const baseInstances = [...prev.boardInstances];
        for (let i = 0; i < newCount - typeInstances.length; i++) {
          const instance = createInstance(boardType, [...baseInstances, ...newInstances]);
          newInstances.push(instance);
        }
        return {
          ...prev,
          boardInstances: [...prev.boardInstances, ...newInstances]
        };
      } else if (newCount < typeInstances.length) {
        const toRemove = typeInstances.length - newCount;
        const idsToRemove = typeInstances.slice(-toRemove).map(i => i.id);
        return {
          ...prev,
          boardInstances: prev.boardInstances.filter(i => !idsToRemove.includes(i.id))
        };
      }
      return prev;
    });
  };

  const handleInstanceConfigChange = (instanceId, field, value) => {
    setFormData(prev => ({
      ...prev,
      boardInstances: prev.boardInstances.map(instance =>
        instance.id === instanceId
          ? { ...instance, [field]: value }
          : instance
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${API_URL}/api/admin/onboarding/gyms`,
        {
          name: formData.name,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          phone: formData.phone,
          managerName: formData.managerName,
          managerEmail: formData.managerEmail,
          managerPhone: formData.managerPhone,
          boards: formData.boardInstances.map(({ id, ...instance }) => instance),
          pricing: {
            planType: formData.planType,
            priceCents: parseInt(formData.priceCents),
            trialDays: parseInt(formData.trialDays)
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create gym');
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!result?.gym?.id) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/onboarding/gyms/${result.gym.id}/send-invite`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.emailSent) {
        setEmailSent(true);
      }
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send email');
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      managerName: '',
      managerEmail: '',
      managerPhone: '',
      boards: [],
      boardInstances: [],
      planType: 'founder',
      priceCents: 4900,
      trialDays: 30
    });
    setResult(null);
    setError(null);
    setEmailSent(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (result) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-xl p-6 border-2 border-slate-900 shadow-retro">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-1 h-8 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
            <h2 className="text-3xl font-bold text-slate-900">Gym Created Successfully!</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-2">Gym Details</h3>
              <p className="text-lg font-semibold">{result.gym.name}</p>
              <p className="text-sm text-slate-600">ID: {result.gym.id}</p>
              <p className="text-sm text-slate-600">Status: {result.gym.status}</p>
            </div>

            <div>
              <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-2">Magic Link</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={result.magicLink}
                  readOnly
                  className="flex-1 px-4 py-2 border-2 border-slate-900 rounded-lg font-mono text-sm"
                />
                <button
                  onClick={() => copyToClipboard(result.magicLink)}
                  className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="pt-4 border-t-2 border-slate-900">
              {!emailSent ? (
                <button
                  onClick={handleSendEmail}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all disabled:opacity-50"
                >
                  {loading ? 'Sending...' : `Send Welcome Email to ${formData.managerEmail}`}
                </button>
              ) : (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-800 border-2 border-teal-500 rounded-full font-bold">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Email Sent!
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="p-4 bg-coral-100 border-2 border-coral-500 rounded-lg">
                <p className="text-coral-800 font-semibold">{error}</p>
              </div>
            )}

            <button
              onClick={handleReset}
              className="w-full px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
            >
              Create Another Gym
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-xl p-6 border-2 border-slate-900 shadow-retro">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-1 h-8 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
          <h2 className="text-3xl font-bold text-slate-900">Provision New Gym</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Gym Information */}
          <div>
            <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-teal-500 rounded"></span>
              Gym Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Gym Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Elevation Bouldering"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Address Line 1 *
                </label>
                <input
                  type="text"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="123 Main St"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Address Line 2
                </label>
                <input
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Suite 200, Unit B, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="CO"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  ZIP Code
                </label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Gym Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="+13035551234"
                />
              </div>
            </div>
          </div>

          {/* Manager Information */}
          <div>
            <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-magenta-500 rounded"></span>
              Manager Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Manager Name
                </label>
                <input
                  type="text"
                  name="managerName"
                  value={formData.managerName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Sarah Johnson"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Manager Email *
                </label>
                <input
                  type="email"
                  name="managerEmail"
                  value={formData.managerEmail}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="sarah@elevationbouldering.com"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Manager Phone
                </label>
                <input
                  type="tel"
                  name="managerPhone"
                  value={formData.managerPhone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="+13035551234"
                />
              </div>
            </div>
          </div>

          {/* Board Selection */}
          <div>
            <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-purple-500 rounded"></span>
              Training Boards *
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {boardOptions.map(board => {
                const count = formData.boardInstances.filter(i => i.type === board.value).length;
                return (
                  <button
                    key={board.value}
                    type="button"
                    onClick={() => handleBoardToggle(board.value)}
                    className={`px-4 py-3 rounded-lg border-2 font-bold transition-all ${
                      formData.boards.includes(board.value)
                        ? 'bg-purple-500 text-white border-slate-900 shadow-retro'
                        : 'bg-white text-slate-700 border-slate-900 hover:bg-purple-50'
                    }`}
                  >
                    {board.label}
                    {count > 0 && <span className="ml-1 opacity-80">({count})</span>}
                  </button>
                );
              })}
            </div>

            {/* Board Instances Configuration */}
            {formData.boards.length > 0 && (
              <div className="space-y-6 mt-6">
                <p className="text-sm text-slate-600 font-semibold">
                  Configure board details (managers will confirm during onboarding):
                </p>
                {formData.boards.map(boardType => {
                  const instances = formData.boardInstances.filter(i => i.type === boardType);
                  const boardLabel = boardOptions.find(b => b.value === boardType)?.label;

                  return (
                    <div key={boardType}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-bold text-slate-900">{boardLabel}</h4>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">Qty:</span>
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(boardType, -1)}
                            disabled={instances.length <= 1}
                            className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-slate-900 font-bold text-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            -
                          </button>
                          <span className="w-8 text-center font-bold">{instances.length}</span>
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(boardType, 1)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-slate-900 font-bold text-lg hover:bg-slate-100"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {instances.map((instance) => (
                          <div key={instance.id} className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-slate-700 mb-1">
                                  Board Name
                                </label>
                                <input
                                  type="text"
                                  value={instance.name}
                                  onChange={(e) => handleInstanceConfigChange(instance.id, 'name', e.target.value)}
                                  className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium text-sm"
                                  placeholder="e.g., Kilter Board - Upstairs, Tension Board 2"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">
                                  Angle (degrees)
                                </label>
                                <input
                                  type="number"
                                  value={instance.angle || ''}
                                  onChange={(e) => handleInstanceConfigChange(instance.id, 'angle', e.target.value)}
                                  className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium text-sm"
                                  placeholder="e.g., 40"
                                  disabled={instance.isAdjustable}
                                />
                              </div>

                              <div className="flex items-center">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={instance.isAdjustable || false}
                                    onChange={(e) => handleInstanceConfigChange(instance.id, 'isAdjustable', e.target.checked)}
                                    className="mr-2 h-4 w-4"
                                  />
                                  <span className="text-sm font-semibold text-slate-700">Adjustable Angle</span>
                                </label>
                              </div>

                              {instance.isAdjustable && (
                                <>
                                  <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                                      Min Angle
                                    </label>
                                    <input
                                      type="number"
                                      value={instance.minAngle || ''}
                                      onChange={(e) => handleInstanceConfigChange(instance.id, 'minAngle', e.target.value)}
                                      className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium text-sm"
                                      placeholder="e.g., 25"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                                      Max Angle
                                    </label>
                                    <input
                                      type="number"
                                      value={instance.maxAngle || ''}
                                      onChange={(e) => handleInstanceConfigChange(instance.id, 'maxAngle', e.target.value)}
                                      className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium text-sm"
                                      placeholder="e.g., 70"
                                    />
                                  </div>
                                </>
                              )}

                              <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-slate-700 mb-1">
                                  Location Notes
                                </label>
                                <input
                                  type="text"
                                  value={instance.locationNotes || ''}
                                  onChange={(e) => handleInstanceConfigChange(instance.id, 'locationNotes', e.target.value)}
                                  className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium text-sm"
                                  placeholder="e.g., Back corner near bathroom, Upstairs by windows"
                                />
                              </div>

                              <div className="flex items-center">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={instance.hasLed || false}
                                    onChange={(e) => handleInstanceConfigChange(instance.id, 'hasLed', e.target.checked)}
                                    className="mr-2 h-4 w-4"
                                  />
                                  <span className="text-sm font-semibold text-slate-700">Has LED Integration</span>
                                </label>
                              </div>

                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">
                                  Initial Status
                                </label>
                                <select
                                  value={instance.status || 'active'}
                                  onChange={(e) => handleInstanceConfigChange(instance.id, 'status', e.target.value)}
                                  className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium text-sm"
                                >
                                  <option value="active">Active (available for booking)</option>
                                  <option value="inactive">Inactive (visible but not bookable)</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pricing */}
          <div>
            <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-sunshine-500 rounded"></span>
              Pricing
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Plan Type
                </label>
                <select
                  name="planType"
                  value={formData.planType}
                  onChange={(e) => {
                    handleChange(e);
                    // Auto-set price to 0 when free plan is selected
                    if (e.target.value === 'free') {
                      setFormData(prev => ({ ...prev, priceCents: 0 }));
                    }
                  }}
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="founder">Founder ($49/mo)</option>
                  <option value="monthly">Monthly (Standard)</option>
                  <option value="annual">Annual</option>
                  <option value="custom">Custom</option>
                  <option value="free">Free (No Payment)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Price (cents)
                </label>
                <input
                  type="number"
                  name="priceCents"
                  value={formData.priceCents}
                  onChange={handleChange}
                  required
                  disabled={formData.planType === 'free'}
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
                  placeholder="4900"
                />
                <p className="text-xs text-slate-600 mt-1">
                  {formData.planType === 'free'
                    ? 'Free plan - no payment required'
                    : `= $${(formData.priceCents / 100).toFixed(2)}/month`}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Trial Days
                </label>
                <input
                  type="number"
                  name="trialDays"
                  value={formData.trialDays}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="30"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-coral-100 border-2 border-coral-500 rounded-lg">
              <p className="text-coral-800 font-semibold">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || formData.boardInstances.length === 0}
            className="w-full px-6 py-4 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Gym...' : 'Create Gym & Generate Magic Link'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default GymProvisioning;
